import type { Path } from "react-hook-form"

type Property<Root, Value> = {
	readonly _path: Path<Root>
} & ArrayOrObjectType<Root, NonNullable<Value>>

export const propertiesOf = <T>(_obj?: T) => pathProxy() as Property<T, T>

type ArrayOrObjectType<Root, Value> = Value extends boolean | number | string | null | undefined
	? Record<string, unknown>
	: Value extends ArrayLike<infer S>
		? { _index: (index: number) => Property<Root, S> }
		: Value extends Record<string, infer S>
			? { _get: (key: string) => Property<Root, S> } & Properties<Root, Value>
			: Properties<Root, Value>

type Properties<Root, Value> = {
	readonly [P in keyof Value & string]: Property<Root, Value[P]>
}

const pathProxy = (property?: Property<any, any>): any =>
	new Proxy(
		{},
		{
			get: (_, childProp) => {
				if (childProp === "_path") {
					return property?._path
				}
				if (childProp === "_index") {
					return (index: number) =>
						pathProxy({
							_path: property ? `${property?._path}.${index}` : index.toString(),
						})
				}
				if (childProp === "_get") {
					return (key: string) =>
						pathProxy({
							_path: property ? `${property?._path}.${key}` : key,
						})
				}
				return pathProxy(
					property
						? {
								_path: `${property?._path}.${childProp.toString()}`,
							}
						: { _path: childProp.toString() },
				)
			},
		},
	)
